import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box, Container, Typography, Paper, Grid, Card, CardContent, CardActions,
    Button, Chip, Avatar, CircularProgress, FormControl, InputLabel, 
    Select, MenuItem, LinearProgress, Divider, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Stack, IconButton, Tooltip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    School as SchoolIcon,
    Person as PersonIcon,
    Grade as GradeIcon,
    Book as BookIcon,
    FilterList as FilterListIcon,
    Assessment as AssessmentIcon,
    Analytics as AnalyticsIcon,
    Print as PrintIcon,
    ChevronRight as ChevronRightIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    Event as EventIcon,
    Storage as StorageIcon,
    TableChart as TableChartIcon,
    CalendarToday as CalendarIcon,
    Search as SearchIcon,
    Category as CategoryIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { formatDatePart } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const AnimatedBox = styled(Box)`animation: ${fadeIn} 0.5s ease-out;`;

const AdminResults = () => {
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);

    const [viewMode, setViewMode] = useState('classes'); // 'classes', 'selection', 'ledger'
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedResultDate, setSelectedResultDate] = useState('');
    const [selectedExamType, setSelectedExamType] = useState('');
    
    const [ledgerData, setLedgerData] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [subjectInfo, setSubjectInfo] = useState({});
    const [loading, setLoading] = useState(false);
    
    const { showToast } = useToast();

    const schoolId = currentUser?._id;
    const schoolName = currentUser?.schoolName || "Academic Institution";
    const examTypes = ['First Terminal', 'Second Terminal', 'Mid-Terminal', 'Annual', 'Test'];

    useEffect(() => {
        if (schoolId && viewMode === 'classes') {
            fetchClasses();
        }
    }, [schoolId, viewMode]);

    const fetchClasses = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/SclassList/${schoolId}`);
            if (Array.isArray(result.data)) setClasses(result.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
        setLoading(false);
    };

    const fetchAvailableDates = async (classId) => {
        setLoading(true);
        try {
            const studentsResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${classId}`);
            if (Array.isArray(studentsResult.data)) {
                const studentsData = studentsResult.data;
                const datesSet = new Set();
                for (let i = 0; i < Math.min(studentsData.length, 10); i++) {
                    const marksResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Marks/Student/${studentsData[i]._id}`);
                    if (Array.isArray(marksResult.data)) {
                        marksResult.data.forEach(m => {
                            if (m.examDate) datesSet.add(m.examDate.split('T')[0]);
                        });
                    }
                }
                setAvailableDates(Array.from(datesSet).sort((a, b) => new Date(b) - new Date(a)));
            }
        } catch (error) {
            console.error('Error fetching dates:', error);
        }
        setLoading(false);
    };

    const fetchLedger = async (classId, examType, resultDate) => {
        setLoading(true);
        try {
            const studentsResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${classId}`);
            if (!Array.isArray(studentsResult.data)) return;

            const studentsData = studentsResult.data;
            const tempLedger = [];
            const tempSubjects = new Set();
            const tempSubjectInfo = {};

            for (const student of studentsData) {
                try {
                    const marksResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Marks/Student/${student._id}`);
                    let marks = Array.isArray(marksResult.data) ? marksResult.data : [];
                    marks = marks.filter(m => m.examDate && m.examDate.startsWith(resultDate) && m.examType?.toLowerCase() === examType.toLowerCase());

                    const studentRow = { id: student._id, name: student.name, rollNum: student.rollNum, marks: {}, totalObtained: 0, totalMax: 0 };
                    marks.forEach(m => {
                        const subName = m.subject?.subName || 'Unknown';
                        tempSubjects.add(subName);
                        studentRow.marks[subName] = m.marksObtained;
                        studentRow.totalObtained += m.marksObtained;
                        studentRow.totalMax += m.maxMarks;
                        if (!tempSubjectInfo[subName]) {
                            tempSubjectInfo[subName] = { fm: m.maxMarks || 100, pm: m.passMarks || 40 };
                        }
                    });

                    studentRow.percentage = studentRow.totalMax > 0 ? ((studentRow.totalObtained / studentRow.totalMax) * 100).toFixed(1) : '0.0';
                    studentRow.grade = calculateGrade(parseFloat(studentRow.percentage));
                    tempLedger.push(studentRow);
                } catch (err) { console.error(err); }
            }

            setSubjects(Array.from(tempSubjects));
            setSubjectInfo(tempSubjectInfo);
            setLedgerData(tempLedger);
            setViewMode('ledger');
        } catch (error) { console.error(error); }
        setLoading(false);
    };

    const calculateGrade = (percentage) => {
        if (percentage >= 90) return 'A+';
        if (percentage >= 80) return 'A';
        if (percentage >= 70) return 'B+';
        if (percentage >= 60) return 'B';
        if (percentage >= 50) return 'C+';
        if (percentage >= 40) return 'C';
        return 'F';
    };

    const handleBack = () => {
        if (viewMode === 'ledger') setViewMode('selection');
        else if (viewMode === 'selection') setViewMode('classes');
    };

    const handleClassSelect = (cls) => {
        setSelectedClass(cls);
        fetchAvailableDates(cls._id);
        setViewMode('selection');
    };

    const handleSearch = () => {
        if (!selectedExamType || !selectedResultDate) {
            showToast("Please select both Exam Type and Result Date", "warning");
            return;
        }
        fetchLedger(selectedClass._id, selectedExamType, selectedResultDate);
    };

    const exportToDocx = () => {
        const header = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
            <head><meta charset='utf-8'><title>Academic Result</title>
            <style>
                body { font-family: 'Calibri', sans-serif; text-align: center; }
                table { border-collapse: collapse; width: 100%; margin-top: 20px; }
                th, td { border: 1px solid black; padding: 8px; text-align: center; font-size: 11px; }
                th { background-color: #f2f2f2; font-weight: bold; }
                .header-info { margin-bottom: 20px; }
                h1 { margin: 0; font-size: 24px; }
                h3 { margin: 5px 0; color: #555; }
            </style>
            </head>
            <body>
                <div class="header-info">
                    <h1>${schoolName}</h1>
                    <h3>ACADEMIC RESULT: ${selectedExamType}</h3>
                    <p><strong>Class:</strong> ${selectedClass?.sclassName} &nbsp;&nbsp;&nbsp; <strong>Date:</strong> ${formatDatePart(selectedResultDate)}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th rowspan="2">Student Name</th>
                            <th rowspan="2">Roll</th>
                            ${subjects.map(sub => `<th colspan="3">${sub}</th>`).join('')}
                            <th rowspan="2">Total</th>
                            <th rowspan="2">%</th>
                            <th rowspan="2">Grade</th>
                        </tr>
                        <tr>
                            ${subjects.map(() => `<th>FM</th><th>PM</th><th>OBT</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${ledgerData.map(row => `
                            <tr>
                                <td style="text-align: left; font-weight: bold;">${row.name}</td>
                                <td>${row.rollNum}</td>
                                ${subjects.map(sub => `
                                    <td>${subjectInfo[sub]?.fm}</td>
                                    <td>${subjectInfo[sub]?.pm}</td>
                                    <td style="${row.marks[sub] < subjectInfo[sub]?.pm ? 'color: red; font-weight: bold;' : ''}">${row.marks[sub] !== undefined ? row.marks[sub] : '-'}</td>
                                `).join('')}
                                <td style="font-weight: bold;">${row.totalObtained}</td>
                                <td style="font-weight: bold;">${row.percentage}%</td>
                                <td style="font-weight: bold;">${row.grade}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        const blob = new Blob(['\ufeff', header], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Result_${selectedClass?.sclassName}_${selectedExamType}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <PageContainer>
            <Box className="no-print">
                <HeaderWrapper elevation={0}>
                    <Container maxWidth="xl">
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 4 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                {viewMode !== 'classes' && (
                                    <IconButton onClick={handleBack} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                )}
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>Academic Results</Typography>
                                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {viewMode === 'classes' ? 'Select a class to generate results' : 
                                         viewMode === 'selection' ? `Class ${selectedClass?.sclassName} • Configure Result` : 
                                         `${selectedExamType} results for Class ${selectedClass?.sclassName}`}
                                    </Typography>
                                </Box>
                            </Stack>
                            {viewMode === 'ledger' && (
                                <Stack direction="row" spacing={1}>
                                    <IconButton onClick={exportToDocx} title="Download Word" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                                        <DownloadIcon fontSize="large" />
                                    </IconButton>
                                  
                                </Stack>
                            )}
                        </Stack>
                    </Container>
                </HeaderWrapper>
            </Box>

            <Container maxWidth="xl" sx={{ mt: 5, pb: 10 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#667eea', mb: 2 }} />
                        <Typography color="textSecondary">Processing data...</Typography>
                    </Box>
                ) : (
                    <>
                        {viewMode === 'classes' && (
                            <Box className="no-print">
                                <Grid container spacing={3}>
                                    {classes.map((cls) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={cls._id}>
                                            <ClassCard onClick={() => handleClassSelect(cls)}>
                                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                                    <IconWrapper color="#667eea"><SchoolIcon fontSize="large" /></IconWrapper>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 2 }}>Class {cls.sclassName}</Typography>
                                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Result View</Typography>
                                                </CardContent>
                                            </ClassCard>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {viewMode === 'selection' && (
                            <Box className="no-print" sx={{ maxWidth: 700, mx: 'auto' }}>
                                <AnimatedBox>
                                    <Paper sx={{ p: 5, borderRadius: '24px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                                            <IconWrapper color="#764ba2"><CategoryIcon fontSize="large" /></IconWrapper>
                                            <IconWrapper color="#667eea"><CalendarIcon fontSize="large" /></IconWrapper>
                                        </Stack>
                                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>Configure Result</Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 5 }}>Select examination type and date</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Exam Type</InputLabel>
                                                    <Select value={selectedExamType} label="Exam Type" onChange={(e) => setSelectedExamType(e.target.value)} sx={{ borderRadius: '12px', textAlign: 'left' }}>
                                                        {examTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Result Date</InputLabel>
                                                    <Select value={selectedResultDate} label="Result Date" onChange={(e) => setSelectedResultDate(e.target.value)} sx={{ borderRadius: '12px', textAlign: 'left' }}>
                                                        {availableDates.map(date => <MenuItem key={date} value={date}>{formatDatePart(date)}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        <Button fullWidth variant="contained" size="large" startIcon={<TableChartIcon />} onClick={handleSearch} disabled={!selectedExamType || !selectedResultDate} sx={{ mt: 5, py: 2, borderRadius: '12px', fontWeight: 800, bgcolor: '#1a1a2e' }}>Generate Result</Button>
                                    </Paper>
                                </AnimatedBox>
                            </Box>
                        )}

                        {viewMode === 'ledger' && (
                            <Box sx={{ width: '100%' }}>
                                <Box className="print-only" sx={{ mb: 4, textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>{schoolName}</Typography>
                                    <Typography variant="h6" sx={{ mb: 2 }}>ACADEMIC RESULT: {selectedExamType}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 5, mb: 2 }}>
                                        <Typography variant="body1"><strong>Class:</strong> {selectedClass?.sclassName}</Typography>
                                        <Typography variant="body1"><strong>Date:</strong> {formatDatePart(selectedResultDate)}</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 3, borderBottomWidth: 2, borderColor: 'black' }} />
                                </Box>

                                <AnimatedBox>
                                    <StyledTableContainer component={Paper} elevation={3} className="docs-table">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow className="docs-header">
                                                    <StyledTableCell rowSpan={2}>Student Name</StyledTableCell>
                                                    <StyledTableCell rowSpan={2}>Roll</StyledTableCell>
                                                    {subjects.map(sub => (
                                                        <StyledTableCell key={sub} align="center" colSpan={3}>{sub}</StyledTableCell>
                                                    ))}
                                                    <StyledTableCell align="center" rowSpan={2}>Total</StyledTableCell>
                                                    <StyledTableCell align="center" rowSpan={2}>%</StyledTableCell>
                                                    <StyledTableCell align="center" rowSpan={2}>Grade</StyledTableCell>
                                                </TableRow>
                                                <TableRow className="docs-header">
                                                    {subjects.map(sub => (
                                                        <React.Fragment key={`${sub}-header`}>
                                                            <StyledTableCell align="center" sx={{ fontSize: '10px !important' }}>FM</StyledTableCell>
                                                            <StyledTableCell align="center" sx={{ fontSize: '10px !important' }}>PM</StyledTableCell>
                                                            <StyledTableCell align="center" sx={{ fontSize: '10px !important' }}>OBT</StyledTableCell>
                                                        </React.Fragment>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {ledgerData.map((row) => (
                                                    <TableRow key={row.id}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                                                        <TableCell>{row.rollNum}</TableCell>
                                                        {subjects.map(sub => (
                                                            <React.Fragment key={`${row.id}-${sub}`}>
                                                                <TableCell align="center">{subjectInfo[sub]?.fm}</TableCell>
                                                                <TableCell align="center">{subjectInfo[sub]?.pm}</TableCell>
                                                                <TableCell align="center">
                                                                    <Typography sx={{ fontWeight: 800, color: row.marks[sub] < subjectInfo[sub]?.pm ? '#d32f2f' : 'inherit' }}>
                                                                        {row.marks[sub] !== undefined ? row.marks[sub] : '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                            </React.Fragment>
                                                        ))}
                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>{row.totalObtained}</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>{row.percentage}%</TableCell>
                                                        <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>{row.grade}</Typography></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </StyledTableContainer>
                                </AnimatedBox>
                            </Box>
                        )}
                    </>
                )}
            </Container>

            <GlobalPrintStyles />
        </PageContainer>
    );
};

export default AdminResults;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 40px;`;
const ClassCard = styled(Paper)`border-radius: 20px !important; cursor: pointer; transition: 0.3s; border: 1px solid #e2e8f0; &:hover { transform: translateY(-5px); box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important; border-color: #667eea; }`;
const IconWrapper = styled(Box)`width: 80px; height: 80px; border-radius: 24px; background: ${props => props.color}15; color: ${props => props.color}; display: flex; align-items: center; justify-content: center; margin: 0 auto;`;
const StyledTableContainer = styled(TableContainer)`border-radius: 20px !important; overflow: hidden; border: 1px solid #e2e8f0;`;
const StyledTableCell = styled(TableCell)`font-weight: 800 !important; padding: 12px 8px !important; white-space: nowrap; border: 1px solid #e2e8f0 !important;`;

const GlobalPrintStyles = styled.div`
    @media print {
        header, nav, aside, footer, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root, .no-print, [role="navigation"], [role="banner"], .MuiBox-root:has(.no-print) { display: none !important; visibility: hidden !important; height: 0 !important; width: 0 !important; position: absolute !important; top: -9999px !important; }
        .print-only { display: block !important; }
        body { background: white !important; padding: 0 !important; margin: 0 !important; }
        #root > div > div > main, main, .MuiBox-root { margin-left: 0 !important; margin-top: 0 !important; padding: 0 !important; width: 100% !important; }
        .MuiContainer-root { max-width: 100% !important; padding: 0 !important; margin: 0 !important; }
        .docs-table { border: none !important; box-shadow: none !important; border-radius: 0 !important; margin-top: 0 !important; }
        .docs-header th { color: black !important; background: #eee !important; border: 1px solid #333 !important; font-weight: bold !important; font-size: 10px !important; }
        td, th { border: 1px solid #333 !important; padding: 4px !important; color: black !important; font-size: 9px !important; }
        @page { size: landscape; margin: 0.5cm; }
        .MuiChip-root { display: none !important; }
    }
    @media screen { .print-only { display: none; } }
`;
